import arrayLength from "@-/com.npmjs.extra-array__length";

import { isScorableToken, type SentenceDrillToken } from "#sentence-drill-token";

/**
 * Which input mode the drill is being answered in. Decides what a hint SHOWS.
 *
 *   * `speech` — the user says the sentence. The hint is the GLYPH: showing the pinyin would tell
 *     them exactly how to pronounce the word, which is the very thing being tested.
 *   * `glyphs` — the user writes the sentence. The hint is the PINYIN: they must produce the
 *     characters, so a reading is a legitimate scaffold rather than the answer.
 */
export type SentenceDrillMode = "glyphs" | "speech";

export type SentenceDrillTokenStatus =
  | { kind: "pending" }
  /** Produced correctly (or non-scorable). Both glyph and pinyin are shown. */
  | { kind: "filled" }
  /**
   * A hint is showing. `revealedCharacterCount` is how many of the word's leading characters the
   * hint has uncovered — escalation walks it up for a multi-character word, so a long word gives
   * ground one character at a time instead of all at once.
   */
  | { kind: "hinted"; revealedCharacterCount: number }
  /** Force-revealed and bolded. This word is rated Again. */
  | { kind: "revealed" };

export type SentenceDrillState = {
  /**
   * The first token the user still owes, or `tokens.length` when the sentence is complete.
   *
   * DERIVED on every transition rather than stored independently — a separately-maintained cursor
   * drifts the moment any transition forgets to move it, and the bug is invisible until the drill
   * asks for a blank that is already filled.
   */
  cursorIndex: number;
  /** Set once "reveal" is pressed: the drill is over and awaiting a rating. */
  isFinished: boolean;
  mode: SentenceDrillMode;
  /** Parallel to `tokens`. */
  statusList: readonly SentenceDrillTokenStatus[];
  tokens: readonly SentenceDrillToken[];
};

/** Whether this token still needs producing. `revealed` counts as done — it is over, just wrongly. */
export function isTokenSettled(status: SentenceDrillTokenStatus): boolean {
  return status.kind === "filled" || status.kind === "revealed";
}

/** The first unsettled scorable token, or `tokens.length` when there is none left. */
export function deriveCursorIndex({
  statusList,
  tokens,
}: {
  statusList: readonly SentenceDrillTokenStatus[];
  tokens: readonly SentenceDrillToken[];
}): number {
  for (let index = 0; index < arrayLength(tokens); index++) {
    const token = tokens[index];
    const status = statusList[index];
    if (token === undefined || status === undefined) {
      continue;
    }
    if (isScorableToken(token) && !isTokenSettled(status)) {
      return index;
    }
  }
  return arrayLength(tokens);
}

/**
 * Start a drill over a segmented sentence.
 *
 * Non-scorable tokens (punctuation, latin) start `filled`: they are not something the user produces,
 * and leaving them `pending` would park the cursor on a blank that can never be satisfied.
 */
export function createSentenceDrillState({
  mode,
  tokens,
}: {
  mode: SentenceDrillMode;
  tokens: readonly SentenceDrillToken[];
}): SentenceDrillState {
  const statusList: SentenceDrillTokenStatus[] = tokens.map((token) =>
    isScorableToken(token) ? { kind: "pending" } : { kind: "filled" },
  );
  return {
    cursorIndex: deriveCursorIndex({ statusList, tokens }),
    isFinished: false,
    mode,
    statusList,
    tokens,
  };
}
